/**
 * Webpage Icon Renderer - Webpage node icon
 *
 * Renders a webpage/window icon used for representing webpage nodes
 * in the graph visualization.
 */

import { getIconConfig } from '@/components/icons/nodeIcons/config/iconConfig';
import { getThemeColors } from '@/components/icons/nodeIcons/config/themeColors';
import {
  calculateIconPosition,
  drawCircleBackground,
  setupCanvas,
} from '@/components/icons/nodeIcons/renderers/base';

/**
 * Draw webpage icon on canvas
 *
 * Internal helper function that draws a webpage/window icon
 * with grid divisions.
 *
 * @param ctx - Canvas rendering context
 * @param x - X coordinate for webpage icon
 * @param y - Y coordinate for webpage icon
 * @param size - Size of the webpage icon
 * @param color - Color for the webpage outline
 */
const drawWebpage = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string = '#000000'
): void => {
  const scale = size / 24;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.fillStyle = 'none';

  // Main rounded rectangle
  ctx.beginPath();
  ctx.roundRect(3, 3, 18, 18, 2);
  ctx.stroke();

  // Horizontal divider
  ctx.beginPath();
  ctx.moveTo(3, 9);
  ctx.lineTo(21, 9);
  ctx.stroke();

  // Vertical divider
  ctx.beginPath();
  ctx.moveTo(9, 21);
  ctx.lineTo(9, 9);
  ctx.stroke();

  ctx.restore();
};

/**
 * Create webpage icon data URL
 *
 * Generates a webpage/window icon with circular background for use as
 * a webpage node image in the graph visualization.
 *
 * @param currentTheme - Current theme ('light' or 'dark')
 * @param isHighlighted - Whether the icon should appear highlighted
 * @returns Data URL string of the rendered icon
 */
export const createWebpageIconDataURL = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean = false
): string => {
  const config = getIconConfig('webpage');
  const colors = getThemeColors('webpage', currentTheme, isHighlighted);
  const { canvas, ctx, size, centerX, centerY } = setupCanvas(config);

  // Draw circle background and border
  drawCircleBackground(ctx, centerX, centerY, size, config.scale, colors);

  // Calculate icon position
  const {
    x: webpageX,
    y: webpageY,
    scale: webpageScale,
  } = calculateIconPosition(centerX, centerY, size, config);

  // Draw webpage icon
  drawWebpage(
    ctx,
    webpageX,
    webpageY,
    config.iconWidth * webpageScale,
    colors.icon
  );

  // Convert to data URL with higher quality
  return canvas.toDataURL('image/png', 1.0);
};
